import React, {useCallback, useState} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  SvgIcon,
  DialogTitle,
  IconButton,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Box,
  Divider,
  Stack,
} from '@zoom/react-ui';
import {IconGoogleCalendar} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import ConnectICloudDialog, {
} from './iCloud';
import {
  ModalContentContainer,
  TypographyMainTitle,
  TypographyFooter,
} from './Style';

import {ReactComponent as iCloudCalendar} from 'assets/svg/icloud_calendar.svg';
import {ReactComponent as MailAndCal} from 'assets/svg/mail_calendar.svg';
import {ReactComponent as OutlookCalendar} from 'assets/svg/outlook_calendar.svg';
import {ReactComponent as ZoomAppIcon} from 'assets/svg/zoom-app-icon.svg';
import BetaBadge from 'Components/BetaBadge/BetaBadge';
import {ZmQuaternaryBtn} from 'Components/ZmButtons';
import {ZmDialog} from 'Components/ZmDialogs';
import {connectAccount, selectThirdPartyAccountReachLimit, setSkipBindCalendar} from 'Store/IntegrationStore';
import {PROVIDER_TYPE} from 'Utils/consts';
import {
  canCreateZmail,
  getUserInfo,
  inZoomClient,
  useConnect3rdParty,
  useListenConnectAccountEvent,
} from 'Utils/integration';
import {openUrlInBrowser, WEB_SCHEDULER_URL} from 'Utils/ZoomClientUtils';
import zoomPalette from 'zoomPalette';

const {
  APPLE,
  GOOGLE_CALENDAR,
  OUTLOOK_CALENDAR,
  ZOOM,
} = PROVIDER_TYPE;

const AccountOptionWidget = (props) => {
  const {
    ariaLabel = '',
    disable,
    isBeta,
    logoIcon,
    onClickConnect,
    service,
  } = props;
  const {t} = useTranslation();
  const {goToAuth, isFetchingOAuthURL} = useConnect3rdParty();
  return (<Stack direction="row" spacing={1}
    style={{
      height: '36px',
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Stack direction="row" spacing={1} style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      lineHeight: '20px',
    }}>
      {!!logoIcon && <SvgIcon
        style={{height: '22px', width: '22px'}}
        component={logoIcon}
        inheritViewBox
      />}
      {ariaLabel}
      {isBeta && <BetaBadge sx={{minWidth: '44px'}} />}
    </Stack>
    <Button
      disabled={disable}
      aria-label={`${t('integration.connect')} ${ariaLabel}`}
      onClick={onClickConnect || (() => {
        if (inZoomClient) {
          openUrlInBrowser(WEB_SCHEDULER_URL);
        } else {
          goToAuth(service);
        }
      })}
      sx={{
        'height': '32px',
        'position': 'relative',
        'fontSize': '14px',
        'fontWeight': '400',
        'lineHeight': '20px',
        'padding': '6px 12px',
        'color': '#323539',
        'border': `1px solid ${zoomPalette.gray[50]}`,
        'borderRadius': '8px',
        '&:disabled': {
          cursor: 'not-allowed',
          pointerEvents: 'all !important',
        },
      }}
    >
      {isFetchingOAuthURL ? (
        <CircularProgress
          size={20}
          style={{
            position: 'absolute',
            top: '10px',
            right: '18px',
            color: '#fff',
          }}
        />
      ) :
      t('integration.connect')}
    </Button>
  </Stack>);
};

/**
 * Connect Account Modal Component
 * @param {Object} props
 * @param {String} props.addOnType
 * @param {String} props.location
 * @return {ReactElement}
 */
export default function ConnectAccountWizard(props) {
  const {
    mode = 'modal',
    onClose,
    onSuccess,
    open,
  } = props;
  const dispatch = useDispatch();
  const disabledConnectionServices = useSelector(
    (s) => s.organizationSettingState.disabledConnectionServices.reduce((res, service) => {
      res[service] = true;
      return res;
    }, {}));
  const [showAppleCalendarDialog, setShowAppleCalendarDialog] = useState(false);
  const thirdPartyAccountReachLimit = useSelector(selectThirdPartyAccountReachLimit);
  const isSubmitting = useSelector((state) => state.integrationState.loadingState.isLoading);
  const {
    zmail: userZmail = '',
  } = getUserInfo() || {};
  const zmailConnected = useSelector((s) => s.integrationState.accounts.value?.find((item) => item.service === ZOOM));
  const showZmailBtn = !zmailConnected && (canCreateZmail || userZmail);
  const onConnectZmail = useCallback(async () => {
    if (!userZmail) {
      // no zmail in token, need to create
      document.referrer &&
      window.open(
        `${
          new URL(document.referrer).origin
        }/profile?action=zmail_setup&from=scheduler`
      );
    } else {
      const zmailAccount = {
        mail: userZmail.toLowerCase(),
        service: ZOOM,
      };
      const {meta} = await dispatch(
        connectAccount(zmailAccount)
      );
      if (meta.requestStatus === 'fulfilled') {
        onSuccess?.(zmailAccount);
      }
    }
  }, [userZmail, dispatch, onSuccess]);
  const {t} = useTranslation();
  useListenConnectAccountEvent(onSuccess);

  const content = <>
    <div style={{fontSize: '14px', color: zoomPalette.gray[60], width: '500px', margin: '24px 0 16px'}}>
      {t('integration.connectedCalendarDesc')}
    </div>
    {mode === 'modal' && <SvgIcon
      style={{width: '251px', height: '136px', margin: showZmailBtn? '15px 0 25px': '35px 0 55px'}}
      component={MailAndCal}
      inheritViewBox
    />}
    <Stack direction="column" spacing={1} style={{
    }}>
      <Box sx={{
        padding: '16px 24px',
        border: `1px solid ${zoomPalette.gray[30]}`,
        borderRadius: '12px',
      }}>
        <Stack direction="column" spacing={1} style={{
          width: '486px',
        }}>
          {!!showZmailBtn && <>
            <AccountOptionWidget
              ariaLabel={t('integration.zoomMailAndCalendar')}
              disable={isSubmitting}
              onClickConnect={onConnectZmail}
              logoIcon={ZoomAppIcon}
            />
            <Divider sx={{margin: '12px 0', width: '100%'}} />
          </>}
          {!disabledConnectionServices[GOOGLE_CALENDAR] && <>
            <AccountOptionWidget
              ariaLabel={t('integration.googleCalendar')}
              disable={thirdPartyAccountReachLimit}
              service="google"
              logoIcon={IconGoogleCalendar}
            />
            <Divider sx={{margin: '12px 0', width: '100%'}} />
          </>}
          {!disabledConnectionServices[OUTLOOK_CALENDAR] && <>
            <AccountOptionWidget
              ariaLabel={t('integration.outlookCalendar')}
              disable={thirdPartyAccountReachLimit}
              service="office365"
              logoIcon={OutlookCalendar}
            />
            <Divider fullWidth sx={{margin: '12px 0', width: '100%'}} />
          </>}
          {!disabledConnectionServices[APPLE] && <AccountOptionWidget
            ariaLabel={t('integration.icloudCalendar')}
            onClickConnect={() => setShowAppleCalendarDialog(true)}
            logoIcon={iCloudCalendar}
            isBeta
          />}
        </Stack>
      </Box>
    </Stack>
    <ConnectICloudDialog
      isSubmitting={isSubmitting}
      onClose={() => setShowAppleCalendarDialog(false)}
      onSuccess={onSuccess}
      open={showAppleCalendarDialog}
    />
    {mode === 'plain' && <ZmQuaternaryBtn sx={{marginTop: '10px'}} size="small" onClick={() => {
      onSuccess({});
      dispatch(setSkipBindCalendar(true));
      sessionStorage.setItem('skipBindCalendar', true);
    }}>
      {t('integration.doThisLater')}
    </ZmQuaternaryBtn>}
    {mode !== 'plain' && <TypographyFooter sx={{marginTop: showZmailBtn? '10px': '45px'}}>
      {t('integration.connectCountLimit')}
    </TypographyFooter>}</>
;
  return mode === 'modal' ? (
    <>
      <ZmDialog sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '574px',
            minHeight: '510px',
            paddingBottom: '10px',
          },
          '& .MuiDialogTitle-root': {
            padding: '20px',
          },
        },
      }} open={open}>
        <DialogTitle>
          <IconButton
            aria-label="close"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <ModalContentContainer>
          <TypographyMainTitle>
            {t('integration.addAccount')}
          </TypographyMainTitle>
          {content}
        </ModalContentContainer>
      </ZmDialog>
    </>
  ): content;
}
